import { Handle, Position } from '@xyflow/react'
import { Plug, X, Circle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { GraphNodeData } from '@/types/graph'
import { useCanvasStore } from '@store/canvas'

interface MCPNodeProps {
  data: GraphNodeData
  selected?: boolean
  id: string
}

export default function MCPNode({ data, selected, id }: MCPNodeProps) {
  const { t } = useTranslation()
  const removeNode = useCanvasStore((state) => state.removeNode)

  const toolsArray = data.config?.tools
    ? (Array.isArray(data.config.tools)
        ? data.config.tools
        : Object.keys(data.config.tools))
    : []

  const isConnected = data.config?.connected === true

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    removeNode(id)
  }

  return (
    <div
      className={`px-4 py-3 rounded-lg border-2 bg-card min-w-48 transition-all ${
        selected
          ? 'border-primary ring-2 ring-primary/30'
          : 'border-primary/30 hover:border-primary'
      }`}
    >
      <Handle type="target" position={Position.Top} />

      {/* Header */}
      <div className="flex items-center justify-between mb-2 group">
        <div className="flex items-center gap-2 flex-1">
          <Plug size={16} className="text-accent-foreground flex-shrink-0" />
          <div className="font-bold text-sm text-foreground truncate">
            {data.label || 'MCP'}
          </div>
          <Circle
            size={8}
            className={`flex-shrink-0 ${
              isConnected ? 'fill-success text-success' : 'text-foreground'
            }`}
          />
        </div>
        <button
          onClick={handleDelete}
          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-error-soft rounded text-error"
          title={t('build.deleteNode')}
        >
          <X size={14} />
        </button>
      </div>

      {/* Body */}
      <div className="text-xs text-muted-foreground mb-2 bg-card rounded p-2 max-h-16 overflow-y-auto">
        {toolsArray.length > 0 ? (
          <div className="space-y-1">
            {toolsArray.slice(0, 3).map((tool: string, idx: number) => (
              <div key={idx} className="text-foreground truncate">
                • {typeof tool === 'string' ? tool : String(tool)}
              </div>
            ))}
            {toolsArray.length > 3 && (
              <div className="text-muted-foreground">{t('build.moreTools', { count: toolsArray.length - 3 })}</div>
            )}
          </div>
        ) : (
          <div className="text-muted-foreground">{t('build.noToolsConfigured')}</div>
        )}
      </div>

      {/* Footer */}
      <div className="flex gap-2 text-xs">
        <span className="px-2 py-1 bg-info-soft text-info rounded">
          {t('build.toolsCount', { count: toolsArray.length })}
        </span>
        {data.config?.server && (
          <span className="px-2 py-1 bg-muted text-foreground rounded truncate">
            {String(data.config.server)}
          </span>
        )}
      </div>

      <Handle type="source" position={Position.Bottom} />
    </div>
  )
}
